import assign from "lodash.assign";
import { browserHistory } from "react-router";
import Entitlements from "../services/Entitlements";
import { orderBulkSubscriptionUsers } from "../utils/bulkSubscriptionUtils";
import { setErrorInErrors, clearErrorFromErrors } from "../utils/errorsUtil";
import ErrorMessages from "common/constants/ErrorMessages";
import appPath from "../utils/appPath";
import { SCREENS, STATUS_EXPIRED } from "common/constants/Constants";
import Validation from "common/utils/Validation";

// Actions (must be name-spaced for reducer)

export const MANAGE_BULK_SUBSCRIPTION_SHOW_ERROR = "MANAGE_BULK_SUBSCRIPTION_SHOW_ERROR";
export const MANAGE_BULK_SUBSCRIPTION_CLEAR_ERROR = "MANAGE_BULK_SUBSCRIPTION_CLEAR_ERROR";
export const MANAGE_BULK_SUBSCRIPTION_SET_INFO = "MANAGE_BULK_SUBSCRIPTION_SET_INFO";
export const MANAGE_BULK_SUBSCRIPTION_SET_EMAIL = "MANAGE_BULK_SUBSCRIPTION_SET_EMAIL";
export const MANAGE_BULK_SUBSCRIPTION_SET_USERS = "MANAGE_BULK_SUBSCRIPTION_SET_USERS";
export const MANAGE_BULK_SUBSCRIPTION_SET_ADMINS = "MANAGE_BULK_SUBSCRIPTION_SET_ADMINS";
export const MANAGE_BULK_SUBSCRIPTION_USER_ACTION_SET_SUCCESS = "MANAGE_BULK_SUBSCRIPTION_USER_ACTION_SET_SUCCESS";
export const MANAGE_BULK_SUBSCRIPTION_USER_ACTION_SHOW_ERROR = "MANAGE_BULK_SUBSCRIPTION_USER_ACTION_SHOW_ERROR";

const initialState = {
    errors: {},
    users: [],
    admins: {},
    userActionState: {
        success: {},
        error: {}
    }
};

export function manageBulkSubscriptionReducer(state = initialState, action) {
    switch (action.type) {
        case MANAGE_BULK_SUBSCRIPTION_SHOW_ERROR:
            return assign({}, state, {
                errors: setErrorInErrors(state.errors, action.error)
            });
        case MANAGE_BULK_SUBSCRIPTION_CLEAR_ERROR:
            return assign({}, state, {
                errors: clearErrorFromErrors(state.errors, action.errorType)
            });
        case MANAGE_BULK_SUBSCRIPTION_SET_INFO:
            return assign({}, state, {
                id: action.id,
                name: action.name,
                maxUsersCount: action.maxUsersCount,
                status: action.status
            });
        case MANAGE_BULK_SUBSCRIPTION_SET_EMAIL:
            return assign({}, state, {
                email: action.email
            });
        case MANAGE_BULK_SUBSCRIPTION_SET_USERS:
            return assign({}, state, {
                users: action.users
            });
        case MANAGE_BULK_SUBSCRIPTION_SET_ADMINS:
            return assign({}, state, {
                admins: assign({}, state.admins, {
                    [action.subscriptionId]: action.admins
                })
            });
        case MANAGE_BULK_SUBSCRIPTION_USER_ACTION_SET_SUCCESS:
        case MANAGE_BULK_SUBSCRIPTION_USER_ACTION_SHOW_ERROR:
            return assign({}, state, {
                userActionState: userActionStateReducer(state.userActionState, action)
            });
        default:
            return state;
    }
}

export function userActionStateReducer(state = { success: {}, error: {} }, action) {
    switch (action.type) {
        case MANAGE_BULK_SUBSCRIPTION_USER_ACTION_SET_SUCCESS:
            return assign({}, state, {
                success: action.success
            });
        case MANAGE_BULK_SUBSCRIPTION_USER_ACTION_SHOW_ERROR:
            return assign({}, state, {
                error: action.error
            });
        default:
            return state;
    }
}

export function showError(error) {
    return {
        type: MANAGE_BULK_SUBSCRIPTION_SHOW_ERROR,
        error
    };
}

export function clearError(errorType) {
    return {
        type: MANAGE_BULK_SUBSCRIPTION_CLEAR_ERROR,
        errorType
    };
}

export function setInfo({ id, name, maxUsersCount, status }) {
    return {
        type: MANAGE_BULK_SUBSCRIPTION_SET_INFO,
        id,
        name,
        maxUsersCount,
        status
    };
}

export function setEmail(email) {
    return {
        type: MANAGE_BULK_SUBSCRIPTION_SET_EMAIL,
        email
    };
}

export function setUsers(users) {
    return {
        type: MANAGE_BULK_SUBSCRIPTION_SET_USERS,
        users
    };
}

export function setAdmins(subscriptionId, admins) {
    return {
        type: MANAGE_BULK_SUBSCRIPTION_SET_ADMINS,
        subscriptionId,
        admins
    };
}

export function userActionSuccess(success) {
    return {
        type: MANAGE_BULK_SUBSCRIPTION_USER_ACTION_SET_SUCCESS,
        success
    };
}

export function userActionError(error) {
    return {
        type: MANAGE_BULK_SUBSCRIPTION_USER_ACTION_SHOW_ERROR,
        error
    };
}

export function updateUserRole(subscriberId, role) {
    return (dispatch, getState) => {
        const subscriptionId = getState().manageBulkSubscription.id;

        return Entitlements.updateUserRoleInBulkSubscription(subscriptionId, subscriberId, role)
            .then(() => Entitlements.getBulkSubscriptionUsers(subscriptionId))
            .then(({ users }) => dispatch(setUsers(users)))
            .catch((error) => dispatch(showError(error)));
    };
}

export function resendVerificationEmail(subscriberId) {
    return (dispatch, getState) => {
        const subscriptionId = getState().manageBulkSubscription.id;

        return Entitlements.resendBulkSubscriptionVerificationEmail(subscriptionId, subscriberId)
            .then(() => dispatch(userActionSuccess({
                target: subscriberId,
                type: "resendVerificationEmail",
                message: "Verification email has been resent to this user"
            })))
            .catch(({ errorMessage }) => dispatch(userActionError({
                target: subscriberId,
                type: "resendVerificationEmail",
                message: errorMessage
            })));
    };
}

export function getAdmins(id) {
    return (dispatch) => {
        return Entitlements.getBulkSubscriptionAdmins(id)
            .then(({ users }) => dispatch(setAdmins(id, users.sort(orderBulkSubscriptionUsers))));
    };
}

export function getUsers() {
    return (dispatch, getState) => {
        const {
            manageBulkSubscription: {
                id,
                maxUsersCount
            }
        } = getState();

        return Entitlements.getBulkSubscriptionUsers(id)
            .then(({ users }) => {
                dispatch(clearError("getUsers"));
                dispatch(setUsers(users));

                if (users.length >= maxUsersCount) {
                    dispatch(showError({
                        message: ErrorMessages.BULK_SUBSCRIPTION_MAX_USERS_REACHED,
                        type: "maxUsersReached"
                    }));
                } else {
                    dispatch(clearError("maxUsersReached"));
                }
            })
            .catch((error) => {
                if (error.status === 401) {
                    browserHistory.push(appPath.withoutQuery(SCREENS.ACCOUNT));

                    return;
                }

                dispatch(showError({
                    type: "getUsers",
                    message: error.errorMessage
                }));
            });
    };
}

export function addUser() {
    return (dispatch, getState) => {
        const {
            manageBulkSubscription: {
                id,
                email
            }
        } = getState();

        dispatch(clearError("addUser"));

        return Entitlements.addBulkSubscriptionUser(id, email)
            .then(() => {
                dispatch(setEmail());
                dispatch(getUsers());
            })
            .catch((error) => {
                dispatch(showError({
                    type: "addUser",
                    message: error.errorMessage
                }));
            });
    };
}

export function removeUser(userId) {
    return (dispatch, getState) => {
        const { manageBulkSubscription: { id } } = getState();

        return Entitlements.removeBulkSubscriptionUser(id, userId)
            .then(() => dispatch(clearError("maxUsersReached")))
            .then(() => dispatch(getUsers()))
            .catch((error) => dispatch(userActionError({
                target: userId,
                type: "removeUser",
                message: error.errorMessage
            })));
    };
}

export function setAndValidateEmail(email) {
    return (dispatch) => {
        dispatch(setEmail(email));

        const error = Validation.validateEmail(email);

        if (error) {
            dispatch(showError({ type: "email", message: error.message }));
        } else {
            dispatch(clearError("email"));
        }
    };
}

export function checkSubscriptionStatus() {
    return (dispatch, getState) => {
        if (getState().manageBulkSubscription.status === STATUS_EXPIRED) {
            browserHistory.push(appPath(SCREENS.ACCOUNT));
        }
    };
}

export default {
    setInfo,
    setEmail,
    updateUserRole,
    setUsers,
    showError,
    clearError,
    userActionSuccess,
    userActionError,
    resendVerificationEmail,
    setAdmins,
    getAdmins,
    getUsers,
    addUser,
    removeUser,
    setAndValidateEmail,
    checkSubscriptionStatus
};
